import { useEffect, useRef, useState } from "react";

import { DateTime } from "luxon";
import {
  ChatBubbleLeftEllipsisIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import { IPart } from "../../models/Part";
import ContactFromId from "../contacts/ContactFromId";

interface PartNotesIndicatorProps {
  part: IPart;
  /**
   * When true, show separate pills for part condition (listing) notes vs internal notes.
   * When false, keep a single combined count (legacy behaviour).
   */
  splitIndicators?: boolean;
}

export default function PartNotesIndicator({
  part,
  splitIndicators = false,
}: PartNotesIndicatorProps) {
  const notes = Array.isArray(part.notes) ? part.notes : [];
  const internalNotes = Array.isArray(part.internalNotes)
    ? part.internalNotes
    : [];
  const total = notes.length + internalNotes.length;

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  if (total === 0) return null;

  const hasInternal = internalNotes.length > 0;
  const formatDate = (iso: string) => {
    try {
      return DateTime.fromISO(iso).toFormat("dd LLL yyyy HH:mm");
    } catch {
      return iso;
    }
  };

  const panel = open && (
        <div
          role="dialog"
          onClick={(e) => e.stopPropagation()}
          className="absolute left-0 top-full z-50 mt-1 w-80 rounded-lg border border-slate-200 bg-white p-3 text-left shadow-xl"
        >
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Notes
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close notes"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="max-h-64 space-y-3 overflow-y-auto pr-1">
            {hasInternal && (
              <div>
                <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                  <ExclamationTriangleIcon
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                  Internal ({internalNotes.length})
                </p>
                <ul className="space-y-1.5">
                  {internalNotes.map((note) => (
                    <li
                      key={note.id}
                      className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-slate-700"
                    >
                      <p className="whitespace-pre-wrap">{note.message}</p>
                      <p className="mt-1 text-[11px] text-slate-500">
                        {formatDate(note.date)}
                        {note.createdBy && (
                          <>
                            {" · "}
                            <ContactFromId contactId={note.createdBy} />
                          </>
                        )}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {notes.length > 0 && (
              <div>
                <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-sky-700">
                  <ChatBubbleLeftEllipsisIcon
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                  Part condition ({notes.length})
                </p>
                <ul className="space-y-1.5">
                  {notes.map((note) => (
                    <li
                      key={note.id}
                      className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700"
                    >
                      <p className="whitespace-pre-wrap">{note.message}</p>
                      <p className="mt-1 text-[11px] text-slate-500">
                        {formatDate(note.date)}
                        {note.createdBy && (
                          <>
                            {" · "}
                            <ContactFromId contactId={note.createdBy} />
                          </>
                        )}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      );

  if (splitIndicators) {
    return (
      <div ref={ref} className="relative inline-flex flex-wrap items-center gap-1">
        {notes.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            title={`${notes.length} part condition note${
              notes.length === 1 ? "" : "s"
            } (listing)`}
            aria-label="Part condition notes"
            aria-expanded={open}
            className="inline-flex h-6 items-center gap-0.5 rounded-full bg-sky-100 px-1.5 text-xs font-semibold text-sky-800 ring-1 ring-inset ring-sky-200 transition-colors hover:bg-sky-200 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{notes.length}</span>
          </button>
        )}
        {internalNotes.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            title={`${internalNotes.length} internal note${
              internalNotes.length === 1 ? "" : "s"
            }`}
            aria-label="Internal notes"
            aria-expanded={open}
            className="inline-flex h-6 items-center gap-0.5 rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-800 ring-1 ring-inset ring-amber-200 transition-colors hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{internalNotes.length}</span>
          </button>
        )}
        {panel}
      </div>
    );
  }

  const Icon = hasInternal
    ? ExclamationTriangleIcon
    : ChatBubbleLeftEllipsisIcon;
  const buttonStyle = hasInternal
    ? "bg-amber-100 text-amber-700 ring-amber-200 hover:bg-amber-200"
    : "bg-sky-100 text-sky-700 ring-sky-200 hover:bg-sky-200";
  const tooltip = hasInternal
    ? `${internalNotes.length} internal note${
        internalNotes.length === 1 ? "" : "s"
      }${
        notes.length
          ? ` · ${notes.length} note${notes.length === 1 ? "" : "s"}`
          : ""
      }`
    : `${notes.length} note${notes.length === 1 ? "" : "s"}`;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title={tooltip}
        aria-label={tooltip}
        aria-expanded={open}
        className={classNames(
          "inline-flex h-6 items-center gap-1 rounded-full px-1.5 text-xs font-semibold ring-1 ring-inset transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400",
          buttonStyle
        )}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        <span>{total}</span>
      </button>

      {panel}
    </div>
  );
}
